import { ReactNode, useEffect } from "react";
import Header from "@/components/layout/Header";
import Sidebar from "@/components/layout/Sidebar";
import MobileSidebarModal from "@/components/layout/MobileSidebarModal";
import { useSidebar } from "@/contexts/SidebarContext";
import { useResponsive } from "@/hooks/useMobile";
import { cn } from "@/components/utils/utils";
import { useAuth } from "@/hooks/useAuth";

interface PrivateLayoutProps {
  children: ReactNode;
  className?: string;
  pageTitle?: string;
  pageDescription?: string;
  showBackButton?: boolean;
}

const PrivateLayout = ({ children, className = "", pageTitle, pageDescription, showBackButton = true }: PrivateLayoutProps) => {
  const { isSidebarCollapsed, isMobileSidebarOpen, setMobileSidebarOpen, toggleSidebar } = useSidebar();
  const { isMobile } = useResponsive();
  const { userRole } = useAuth();

  const handleMobileSidebarClose = () => {
    setMobileSidebarOpen(false);
  };

  // Chiudi il mobile sidebar quando si passa da mobile a desktop
  useEffect(() => {
    if (!isMobile && isMobileSidebarOpen) {
      setMobileSidebarOpen(false);
    }
  }, [isMobile, isMobileSidebarOpen, setMobileSidebarOpen]);

  return (
    <div className="min-h-screen bg-white">
      {/* Header Fixed */}
      <Header 
        pageTitle={pageTitle}
        pageDescription={pageDescription}
        showBackButton={showBackButton}
      />

      {/* Mobile Sidebar Modal */}
      <MobileSidebarModal 
        isOpen={isMobileSidebarOpen}
        onClose={handleMobileSidebarClose}
        userRole={userRole}
      />
      
      <div className="flex h-screen">
        {/* Desktop Sidebar */}
        <div className={cn(
          "hidden md:block h-full transition-all duration-300 ease-in-out",
          isSidebarCollapsed ? "w-[70px]" : "w-[208px]"
        )}>
          <Sidebar 
            isCollapsed={isSidebarCollapsed} 
            onToggle={toggleSidebar}
            userRole={userRole}
          />
        </div>
        
        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Main Content */}
          <main className={cn(
            "flex-1 transition-all duration-300 ease-in-out overflow-auto mt-20 md:mt-[100px]",
            className
          )}>
            <div className="container mx-auto px-4 md:px-6 my-4 md:my-10">
              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default PrivateLayout; 